import { getParticipantById } from '../base/participants/functions';

import { OPEN_CHAT } from './actionTypes';
import { getCurrentConference } from '../base/conference/functions';
import { getLocalParticipant } from '../base/participants/functions';

import { LOBBY_CHAT_INITIALIZED } from '../lobby/constants';

import {
  ADD_MESSAGE,
  CLEAR_MESSAGES,
  CLOSE_CHAT,
  EDIT_MESSAGE,
  REMOVE_LOBBY_CHAT_PARTICIPANT,
  SEND_MESSAGE,
  SET_IS_POLL_TAB_FOCUSED,
  SET_LOBBY_CHAT_ACTIVE_STATE,
  SET_LOBBY_CHAT_RECIPIENT,
  SET_PRIVATE_MESSAGE_RECIPIENT
} from './actionTypes';
import VideoLayout from 'util/UI/videolayout/VideoLayout';
import { getLargeVideoParticipant } from '../large-video/functions';
import { fetchUserInfo } from '../login/actions';
import { playSound } from '../base/sounds/actions';
import { KNOCK_ONE_SOUND_ID, KNOCK_TWO_SOUND_ID } from './constants';

/**
 * Adds a chat message to the collection of messages.
 *
 * @param {Object} messageDetails - The chat message to save.
 * @param {string} messageDetails.displayName - The displayName of the
 * participant that authored the message.
 * @param {boolean} messageDetails.hasRead - Whether or not to immediately mark
 * the message as read.
 * @param {string} messageDetails.message - The received message to display.
 * @param {string} messageDetails.messageType - The kind of message, such as
 * "error" or "local" or "remote".
 * @param {string} messageDetails.timestamp - A timestamp to display for when
 * the message was received.
 * @param {string} messageDetails.isReaction - Whether or not the
 * message is a reaction message.
 * @returns {{
 *     type: ADD_MESSAGE,
 *     displayName,
 *     hasRead,
 *     message,
 *     messageType,
 *     timestamp,
 *     isReaction
 * }}
 */
export function addMessage(messageDetails) {
  return {
    type: ADD_MESSAGE,
    ...messageDetails
  };
}

/**
 * Edits an existing chat message.
 *
 * @param {Object} message - The chat message to edit/override. The messages will be matched from the state
 * comparing the messageId.
 * @returns {{
 *     type: EDIT_MESSAGE,
 *     message
 * }}
 */
export function editMessage(message) {
  return {
    type: EDIT_MESSAGE,
    message
  };
}

/**
 * Clears the chat messages in Redux.
 *
 * @returns {{
 *     type: CLEAR_MESSAGES
 * }}
 */
export function clearMessages() {
  return {
    type: CLEAR_MESSAGES
  };
}

/**
 * Action to signal the closing of the chat dialog.
 *
 * @returns {{
 *     type: CLOSE_CHAT
 * }}
 */
export function closeChat() {
  return {
    type: CLOSE_CHAT
  };
}

/**
 * Sends a chat message to everyone in the conference.
 *
 * @param {string} message - The chat message to send out.
 * @param {boolean} ignorePrivacy - True if the privacy notification should be ignored.
 * @returns {{
 *     type: SEND_MESSAGE,
 *     ignorePrivacy,
 *     message
 * }}
 */
export function sendMessage(message, ignorePrivacy = false) {
  return {
    type: SEND_MESSAGE,
    ignorePrivacy,
    message
  };
}

export function sendRemoteAudioCommand(participantId, audioCommand) {
  return async (dispatch, getState) => {
    const state = getState();
    const localParticipant = getLocalParticipant(state);
    const participant = getParticipantById(state, participantId);

    if (audioCommand !== 'join' && audioCommand !== 'leave') return;

    if (!localParticipant?.id || !participant?.id) return;

    const customUserid = participant?.customUserid;

    if (!customUserid || customUserid === 'off') return;

    if (
      participant?.customSleepModeStats === 'on' ||
      participant?.customUserPrivacy === 'on'
    ) {
      return;
    }

    const profile =
      state['features/login'].profiles?.[customUserid] ||
      (await dispatch(fetchUserInfo(customUserid)));

    if (profile?.device?.options?.allowRemoteAudioControl != 1) return;

    const message = {
      EventType: audioCommand === 'join' ? 20 : 19,
      Message: audioCommand === 'join' ? 'Join Audio' : 'Leave Audio',
      FromParticipantID: localParticipant.id,
      ToParticipantID: [participant.id]
    };

    return dispatch(sendMessage(JSON.stringify(message)));
  };
}

export function sendSensorActiveMessage(active) {
  return async (dispatch, getState) => {
    const localParticipant = getLocalParticipant(getState());
    const message = {
      EventType: active ? 23 : 24,
      Message: active ? 'Blur Active' : 'Blur Inactive',
      FromParticipantID: localParticipant?.id
    };
    return dispatch(sendMessage(JSON.stringify(message)));
  };
}

export function sendSingleKnockMessage(participantId) {
  return async (dispatch, getState) => {
    const state = getState();
    const localParticipant = getLocalParticipant(state);
    const targetParticipantId =
      participantId ?? getLargeVideoParticipant(state)?.id;

    if (localParticipant?.id && targetParticipantId) {
      const message = {
        EventType: 15,
        Message: 'Knock Knock',
        FromParticipantID: localParticipant.id,
        ToParticipantID: [targetParticipantId]
      };
      dispatch(playSound(KNOCK_ONE_SOUND_ID));
      return dispatch(sendMessage(JSON.stringify(message)));
    }
  };
}

export function sendMultiKnockMessage(participantId) {
  return (dispatch, getState) => {
    const state = getState();
    const localParticipant = getLocalParticipant(state);
    const targetParticipantId =
      participantId ?? getLargeVideoParticipant(state)?.id;

    if (localParticipant?.id && targetParticipantId) {
      const message = {
        EventType: 14,
        Message: 'Knock Knock',
        FromParticipantID: localParticipant.id,
        ToParticipantID: [targetParticipantId]
      };
      dispatch(playSound(KNOCK_TWO_SOUND_ID));
      return dispatch(sendMessage(JSON.stringify(message)));
    }
  };
}
/**
 * Initiates the sending of a private message to the supplied participant.
 *
 * @param {IParticipant} participant - The participant to set the recipient to.
 * @returns {{
 *     participant: IParticipant,
 *     type: SET_PRIVATE_MESSAGE_RECIPIENT
 * }}
 */
export function setPrivateMessageRecipient(participant) {
  return {
    participant,
    type: SET_PRIVATE_MESSAGE_RECIPIENT
  };
}

/**
 * Set the value of _isPollsTabFocused.
 *
 * @param {boolean} isPollsTabFocused - The new value for _isPollsTabFocused.
 * @returns {Function}
 */
export function setIsPollsTabFocused(isPollsTabFocused) {
  return {
    isPollsTabFocused,
    type: SET_IS_POLL_TAB_FOCUSED
  };
}

/**
 * Initiates the sending of messages between a moderator and a lobby attendee.
 *
 * @param {Object} lobbyChatInitializedInfo - The information about the attendee and the moderator
 * that is going to chat.
 *
 * @returns {Function}
 */
export function onLobbyChatInitialized(lobbyChatInitializedInfo) {
  return async (dispatch, getState) => {
    const state = getState();
    const conference = getCurrentConference(state);

    const lobbyLocalId = conference.myLobbyUserId();

    if (!lobbyLocalId) {
      return;
    }

    if (lobbyChatInitializedInfo.moderator.id === lobbyLocalId) {
      dispatch({
        type: SET_LOBBY_CHAT_RECIPIENT,
        participant: lobbyChatInitializedInfo.attendee,
        open: true
      });
    }

    if (lobbyChatInitializedInfo.attendee.id === lobbyLocalId) {
      return dispatch({
        type: SET_LOBBY_CHAT_RECIPIENT,
        participant: lobbyChatInitializedInfo.moderator,
        open: false
      });
    }
  };
}

/**
 * Sets the lobby room's chat active state.
 *
 * @param {boolean} value - The active state.
 *
 * @returns {Object}
 */
export function setLobbyChatActiveState(value) {
  return {
    type: SET_LOBBY_CHAT_ACTIVE_STATE,
    payload: value
  };
}

/**
 * Removes lobby type messages.
 *
 *  @param {boolean} removeLobbyChatMessages - Should remove messages from chat  (works only for accepted users).
 * If not specified, it will delete all lobby messages.
 *
 * @returns {Object}
 */
export function removeLobbyChatParticipant(removeLobbyChatMessages) {
  return {
    type: REMOVE_LOBBY_CHAT_PARTICIPANT,
    removeLobbyChatMessages
  };
}

/**
 * Handles initial setup of lobby message between
 * Moderator and participant.
 *
 * @param {string} participantId - The participant id.
 *
 * @returns {Object}
 */
export function handleLobbyChatInitialized(participantId) {
  return async (dispatch, getState) => {
    if (!participantId) {
      return;
    }
    const state = getState();
    const conference = state['features/base/conference'].conference;
    const { knockingParticipants } = state['features/lobby'];
    const { lobbyMessageRecipient } = state['features/chat'];
    const me = getLocalParticipant(state);
    const lobbyLocalId = conference?.myLobbyUserId();

    if (lobbyMessageRecipient && lobbyMessageRecipient.id === participantId) {
      return dispatch(setLobbyChatActiveState(true));
    }

    const attendee = knockingParticipants.find((p) => p.id === participantId);

    if (attendee && attendee.chattingWithModerator === lobbyLocalId) {
      return dispatch({
        type: SET_LOBBY_CHAT_RECIPIENT,
        participant: attendee,
        open: true
      });
    }

    if (!attendee) {
      return;
    }

    const payload = {
      type: LOBBY_CHAT_INITIALIZED,
      moderator: {
        ...me,
        name: 'Moderator',
        id: lobbyLocalId
      },
      attendee
    };

    // notify attendee privately.
    conference?.sendLobbyMessage(payload, attendee.id);

    // notify other moderators.
    return conference?.sendLobbyMessage(payload);
  };
}

/**
 * Displays the chat panel.
 *
 * @param {Object} participant - The recipient for the private chat.
 * @returns {{
 *     participant: Participant,
 *     type: OPEN_CHAT
 * }}
 */
export function openChat(participant) {
  return function (dispatch) {
    dispatch({
      participant,
      type: OPEN_CHAT
    });
  };
}

/**
 * Displays the chat panel for a participant identified by an id.
 *
 * @param {string} id - The id of the participant.
 * @returns {{
 *     participant: Participant,
 *     type: OPEN_CHAT
 * }}
 */
export function openChatById(id) {
  return function (dispatch, getState) {
    const participant = getParticipantById(getState(), id);

    return dispatch({
      participant,
      type: OPEN_CHAT
    });
  };
}

/**
 * Toggles display of the chat panel.
 *
 * @returns {Function}
 */
export function toggleChat() {
  return (dispatch, getState) => {
    const isOpen = getState()['features/chat'].isOpen;

    if (isOpen) {
      dispatch(closeChat());
    } else {
      dispatch(openChat());
    }

    // Recompute the large video size whenever we toggle the chat, as it takes chat state into account.
    VideoLayout.onResize();
  };
}
